import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useLogout } from '../api/queries/auth';
import { useHalls } from '../api/queries/halls';
import { useNotifications } from '../api/queries/notifications';
import { OCCASIONS } from '../content/occasions';
import { useT } from '../i18n';
import { CONTACT_EMAIL, CONTACT_PHONE, EMAIL_HREF, OPERATOR, PHONE_HREF } from '../lib/contact';
import { useAuth } from '../providers/AuthProvider';
import { CurtainMark, Logo } from './Brand';
import { SiteMenu } from './SiteMenu';
import { UserMenu } from './UserMenu';
import { placeName } from '../lib/places';

/**
 * Public marketing pages: they carry the design system's element resets (.ds)
 * and run full-bleed from the top of the window, under the fixed header.
 */
const SITE_ROUTE = /^\/(?:$|venues\/?$|occasions(?:\/|$)|locations\/?$|inspiration(?:\/|$)|about\/?$|contact\/?$|privacy\/?$|hall\/|login\/?$)/;

/**
 * Pages that open on a dark night band (the home hero or a page hero): the
 * header starts transparent with light ink over them. Everywhere else it is
 * solid from the start.
 */
const NIGHT_TOP = /^\/(?:$|venues\/?$|occasions(?:\/|$)|locations\/?$|inspiration(?:\/|$)|about\/?$|contact\/?$|privacy\/?$)/;

export function Layout() {
  const { t } = useT();
  const { user, setUser } = useAuth();
  const logout = useLogout();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const { data: halls } = useHalls({});

  const { data: notifications } = useNotifications(Boolean(user));
  const unread = notifications?.filter((n) => !n.read).length ?? 0;

  const isSite = SITE_ROUTE.test(location.pathname);
  const solid = !NIGHT_TOP.test(location.pathname);

  const [menuOpen, setMenuOpen] = useState(false);
  const opener = useRef<HTMLElement | null>(null);

  const setMenu = useCallback((open: boolean) => {
    if (open) opener.current = document.activeElement as HTMLElement | null;
    setMenuOpen(open);
    if (!open) opener.current?.focus({ preventScroll: true });
  }, []);

  // The header turns to glass once the page has scrolled, and both it and the
  // dock slide away while scrolling down, back on the way up.
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    let lastY = window.scrollY;
    let ticking = false;
    const update = () => {
      ticking = false;
      const y = window.scrollY;
      setScrolled(y > 24);
      if (document.body.classList.contains('is-locked') || y < 80) setHidden(false);
      else if (Math.abs(y - lastY) > 6) setHidden(y > lastY);
      lastY = y;
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // A new page never opens with the menu drawn, or the header tucked away.
  useEffect(() => {
    setMenuOpen(false);
    setHidden(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.classList.toggle('is-locked', menuOpen);
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMenu(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen, setMenu]);

  const onLogout = async () => {
    await logout.mutateAsync();
    setUser(null);
    qc.clear(); // drop every cached response belonging to the old session
    navigate('/');
  };

  // "List your venue" means something different to each role.
  const venueLink = !user
    ? { to: '/login?signup=venue', label: t('snListVenue') }
    : user.role === 'manager' || user.role === 'supervisor'
      ? { to: '/manager?tab=halls', label: t('footerMyVenues') }
      : user.role === 'admin'
        ? { to: '/admin/venues', label: t('footerAdminVenues') }
        : null;

  const headerClass = [
    'site-header ds',
    solid && 'is-solid',
    scrolled && 'is-scrolled',
    hidden && !menuOpen && 'is-hidden',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <a className="skip" href="#main">
        {t('snSkip')}
      </a>

      <header className={headerClass}>
        <div className="wrap bar">
          <button
            className="hd-menu"
            type="button"
            aria-label={menuOpen ? t('navCloseMenu') : t('navOpenMenu')}
            aria-expanded={menuOpen}
            aria-controls="site-menu"
            onClick={() => setMenu(!menuOpen)}
          >
            <span className="hb" aria-hidden="true">
              <i />
              <i />
              <i />
            </span>
          </button>
          <Link className="logo" to="/" aria-label={t('appName')}>
            <Logo />
          </Link>
          <div className="header-actions">
            {user ? (
              <>
                {user.role === 'user' && (
                  <NavLink className="text-link hide-md" to="/bookings">
                    {t('navMyBookings')}
                  </NavLink>
                )}
                {user.role === 'admin' && (
                  <NavLink className="text-link hide-md" to="/admin">
                    {t('navAdmin')}
                  </NavLink>
                )}
                <NavLink
                  className="icon-btn hd-bell"
                  to="/notifications"
                  aria-label={unread ? t('navUnread', { count: unread }) : t('navNotifications')}
                >
                  <i className="ri-notification-3-line" aria-hidden="true" />
                  {unread > 0 && <span className="hd-count">{unread > 9 ? '9+' : unread}</span>}
                </NavLink>
                <UserMenu user={user} onLogout={onLogout} />
              </>
            ) : (
              <Link className="hd-login" to="/login">
                <i className="ri-user-line" aria-hidden="true" />
                {t('snLogin')}
              </Link>
            )}
          </div>
        </div>
      </header>

      <SiteMenu
        open={menuOpen}
        onClose={() => setMenu(false)}
        halls={halls ?? []}
        user={user}
        venueLink={venueLink}
        onLogout={onLogout}
      />

      <div className={`dock${hidden && !menuOpen ? ' is-hidden' : ''}`}>
        <button
          className="menu-toggle"
          type="button"
          aria-label={menuOpen ? t('navCloseMenu') : t('navOpenMenu')}
          aria-expanded={menuOpen}
          aria-controls="site-menu"
          onClick={() => setMenu(!menuOpen)}
        >
          <CurtainMark />
          <span>{menuOpen ? t('mmClose') : t('mmMenu')}</span>
          {!menuOpen && unread > 0 && <span className="hd-count dock-count">{unread > 9 ? '9+' : unread}</span>}
          <span className="bars" aria-hidden="true" />
        </button>
      </div>

      <main id="main" className={isSite ? 'ds site-main' : 'container app-main'} tabIndex={-1}>
        {/* Pages load on demand; the header and footer stay put meanwhile. */}
        <Suspense fallback={<p className="muted route-loading" role="status">{t('loading')}</p>}>
          <Outlet />
        </Suspense>
      </main>

      <SiteFooter venueLink={venueLink} signedIn={Boolean(user)} isGuest={!user || user.role === 'user'} />
    </>
  );
}

/** Popular cities, read from the live venue list (most venues first). */
function useTopCities(limit = 6) {
  const { data: halls } = useHalls({});
  return useMemo(() => {
    const counts = new Map<string, number>();
    for (const h of halls ?? []) {
      const city = (h.city || '').trim();
      if (city) counts.set(city, (counts.get(city) ?? 0) + 1);
    }
    return [...counts].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).slice(0, limit).map(([c]) => c);
  }, [halls, limit]);
}

function SiteFooter({
  venueLink,
  signedIn,
  isGuest,
}: {
  venueLink: { to: string; label: string } | null;
  signedIn: boolean;
  isGuest: boolean;
}) {
  const { t, lang } = useT();
  const cities = useTopCities();

  return (
    <footer className="site-footer ds">
      <div className="wrap">
        {/* A way to talk to a person — not a mailing list: the site has none. */}
        <div className="footer-cta">
          <div>
            <strong>{t('ftPlanTitle')}</strong>
            <p>{t('ftPlanBody')}</p>
          </div>
          <Link className="btn btn-primary" to="/contact">
            {t('ftPlanCta')}
            <i className="ri-arrow-right-line i i-move" aria-hidden="true" />
          </Link>
        </div>

        <div className="footer-top">
          <div className="footer-brand">
            <Link className="logo footer-logo" to="/" aria-label={t('appName')}>
              <Logo />
            </Link>
            <div className="tagline">
              {t('ftBrandLineA')} <span className="accent">{t('ftBrandLineB')}</span>
            </div>
            <p>{t('ftTagline')}</p>
            <dl className="footer-facts">
              <div>
                <dt>{t('ftAddress')}</dt>
                <dd>{OPERATOR}, Kalamassery, Kochi</dd>
              </div>
              <div>
                <dt>{t('ftContactLabel')}</dt>
                <dd>
                  <a href={PHONE_HREF}>{CONTACT_PHONE}</a>
                  <br />
                  <a href={EMAIL_HREF}>{CONTACT_EMAIL}</a>
                </dd>
              </div>
            </dl>
          </div>

          <div className="footer-cols">
            <div>
              <h3>{t('ftExplore')}</h3>
              <ul>
                <li>
                  <Link to="/venues">{t('ftAllVenues')}</Link>
                </li>
                {OCCASIONS.slice(0, 5).map((o) => (
                  <li key={o.slug}>
                    <Link to={`/occasions/${o.slug}`}>{t(o.title)}</Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3>{t('ftLocations')}</h3>
              <ul>
                {cities.map((c) => (
                  <li key={c}>
                    <Link to={`/venues?location=${encodeURIComponent(c)}`}>{placeName(c, lang)}</Link>
                  </li>
                ))}
                <li>
                  <Link to="/locations">{t('snLocations')} →</Link>
                </li>
              </ul>
            </div>

            <div>
              <h3>{t('ftResources')}</h3>
              <ul>
                <li>
                  <Link to="/inspiration">{t('ftGuides')}</Link>
                </li>
                <li>
                  <Link to="/about#how">{t('ftHowItWorks')}</Link>
                </li>
                <li>
                  <Link to="/about#faq">{t('ftFaq')}</Link>
                </li>
                {signedIn && isGuest && (
                  <li>
                    <Link to="/bookings">{t('footerMyBookings')}</Link>
                  </li>
                )}
                {signedIn && (
                  <li>
                    <Link to="/notifications">{t('footerNotifications')}</Link>
                  </li>
                )}
              </ul>
            </div>

            <div>
              <h3>{t('ftCompany')}</h3>
              <ul>
                <li>
                  <Link to="/about">{t('ftAbout')}</Link>
                </li>
                <li>
                  <Link to="/contact">{t('ftContact')}</Link>
                </li>
                {venueLink && (
                  <li>
                    <Link to={venueLink.to}>{venueLink.label}</Link>
                  </li>
                )}
                <li>
                  <Link to="/privacy">{t('ftPrivacy')}</Link>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="footer-bottom">
          <span>{t('footerCopyright', { year: String(new Date().getFullYear()) })}</span>
          <span>{t('ftMadeIn')}</span>
        </div>
      </div>
    </footer>
  );
}
